import React, { useState, memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Archive, CheckCircle, Info, ChevronDown, StickyNote, PackageCheck, AlertTriangle, Coins } from 'lucide-react';
import { Order } from '@/supabase/order/types/order';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

interface CombinedShippingModalProps {
  isOpen: boolean;
  orders: Order[];
  nickname: string; // 단일 고객 모드에서 사용
  onClose: () => void;
  onConfirm: (selectedOrderIds: number[]) => void;
  isLoading: boolean;
  sourceOrderCount?: number; // 일괄 처리 모드에서 사용
}

const statusConfig = {
  '미지정': { icon: Info, color: 'text-gray-500' },
  '부분 입금': { icon: Coins, color: 'text-amber-500' },
  '입금 완료': { icon: CheckCircle, color: 'text-green-600' },
  '출고 완료': { icon: Archive, color: 'text-blue-600' },
};

const OrderItemRow: React.FC<{ item: NonNullable<Order['items']>[0] }> = memo(({ item }) => (
  <div className="flex justify-between items-center text-sm text-gray-600 py-1.5 px-2">
    <span className="flex-1 truncate pr-2">{item.item_name}</span>
    <span className="w-16 text-right">{item.price.toLocaleString()}원</span>
    <span className="w-12 text-right">{item.quantity}개</span>
  </div>
));

const OrderCard: React.FC<{ order: Order; onSelectionChange: (orderId: number, isSelected: boolean) => void; isSelected: boolean; }> = memo(({ order, onSelectionChange, isSelected }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const StatusIcon = statusConfig[order.status]?.icon || Info;
  const statusColor = statusConfig[order.status]?.color || 'text-gray-500';

  return (
    <li className={cn("bg-white rounded-lg border transition-shadow hover:shadow-md", isSelected ? "border-indigo-500 shadow-lg" : "border-gray-200/80")}>
      <div className="flex items-center p-3">
        <Checkbox
          id={`select-order-${order.id}`}
          checked={isSelected}
          onCheckedChange={(checked) => onSelectionChange(order.id, !!checked)}
          className="mr-4"
          aria-label={`주문 ${order.id} 선택`}
        />
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left"
          aria-expanded={isExpanded}
        >
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="font-semibold text-gray-800 text-sm flex items-center">
                {format(new Date(order.order_date), 'yyyy. MM. dd')}
                {order.memo && <StickyNote className="w-3.5 h-3.5 ml-1.5 text-amber-600" />}
              </span>
              <span className="text-xs font-medium text-indigo-600 mt-1">{(order.total_price || 0).toLocaleString()}원</span>
            </div>
            <div className="flex items-center space-x-2 text-xs">
              {order.is_keep && (
                <span className="font-semibold text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full">
                  킵
                </span>
              )}
              <span className={cn("font-semibold flex items-center", statusColor)}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {order.status}
              </span>
              <ChevronDown
                className={cn("w-4 h-4 text-gray-400 transition-transform", isExpanded && "rotate-180")}
              />
            </div>
          </div>
        </button>
      </div>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t mx-3 my-1.5"></div>
            <div className="px-3 pb-3 space-y-2">
              {order.memo && (
                <div className="mt-2 p-2 bg-amber-50 border-l-4 border-amber-400 rounded-r-md">
                  <p className="text-xs text-amber-900 whitespace-pre-wrap break-words">{order.memo}</p>
                </div>
              )}
              {order.items && order.items.length > 0 ? (
                <div className="space-y-1">
                  {order.items.map(item => <OrderItemRow key={item.id} item={item} />)}
                </div>
              ) : (
                <div className="text-center text-xs text-gray-500 py-2">상품 정보가 없습니다.</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
});

export const CombinedShippingModal: React.FC<CombinedShippingModalProps> = memo(({
  isOpen,
  isLoading,
  orders,
  nickname,
  onClose,
  onConfirm,
  sourceOrderCount = 1,
}) => {
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);

  const groupedOrders = useMemo(() => {
    return orders.reduce((acc, order) => {
      const key = order.nickname || '알 수 없는 고객';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(order);
      return acc;
    }, {} as Record<string, Order[]>);
  }, [orders]);
 
   const handleSelectionChange = (orderId: number, isSelected: boolean) => {
     setSelectedOrderIds(prev =>
      isSelected ? [...prev, orderId] : prev.filter(id => id !== orderId)
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allOrderIds = orders.map(order => order.id);
      setSelectedOrderIds(allOrderIds);
    } else {
      setSelectedOrderIds([]);
    }
  };

  const allOrdersSelected = useMemo(() =>
    orders.length > 0 && selectedOrderIds.length === orders.length,
    [orders, selectedOrderIds]
  );

  const handleConfirm = () => {
    onConfirm(selectedOrderIds);
    setSelectedOrderIds([]);
  };
  
  const handleClose = () => {
    onClose();
    setSelectedOrderIds([]);
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg w-full bg-gray-50">
        <DialogHeader>
          <DialogTitle className="flex items-center text-lg">
            <PackageCheck className="w-5 h-5 mr-2 text-indigo-600" />
            <span>
             {sourceOrderCount > 1 ? (
               <span className="font-bold text-indigo-700">{sourceOrderCount}건의 주문</span>
             ) : (
               <span className="font-bold text-indigo-700">{nickname}</span>
             )}
              <span className="font-medium text-gray-600">의 합배송 가능 주문</span>
            </span>
          </DialogTitle>
          <DialogDescription className="mt-2">
           아래 목록에서 함께 <strong>'출고 완료'</strong> 처리할 주문을 선택하세요.
          </DialogDescription>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-40 text-gray-500">
            <p>관련 주문을 불러오는 중...</p>
          </div>
        ) : (
          <div className="py-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-md mb-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-yellow-500" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                            선택된 주문들은 현재 주문과 함께 <strong>'출고 완료'</strong> 상태로 변경되며, '킵' 상태였다면 해제됩니다.
                        </p>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-end px-1 mb-2">
              <Checkbox
                id="select-all-orders"
                checked={allOrdersSelected}
                onCheckedChange={(checked) => handleSelectAll(!!checked)}
                aria-label="모든 주문 선택"
              />
              <label htmlFor="select-all-orders" className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
                전체 선택
              </label>
            </div>
            <div className="overflow-y-auto max-h-[50vh] p-1">
              <ul className="space-y-2">
                {Object.entries(groupedOrders).map(([nickname, customerOrders]) => (
                 <div key={nickname}>
                   <h3 className="text-sm font-semibold text-gray-800 my-3 pt-2 border-t">{nickname}</h3>
                   {customerOrders.map((order) => (
                     <OrderCard
                       key={order.id}
                       order={order}
                       onSelectionChange={handleSelectionChange}
                       isSelected={selectedOrderIds.includes(order.id)}
                     />
                   ))}
                 </div>
                ))}
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>취소</Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              "text-white",
              "bg-brand-primary",
              "transition-transform hover:scale-105"
            )}
          >
            <PackageCheck className="w-4 h-4 mr-2" />
            {selectedOrderIds.length > 0
              ? `${selectedOrderIds.length}개 주문 함께 출고 완료`
              : "현재 주문만 출고 완료"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});